import React, { useEffect, useState } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { logout } from '../store/authSlice';
import adminService from '../services/adminService';
import SelectorIdioma from './SelectorIdioma';
import PeuStrategos from './PeuStrategos';
import { useTranslation } from 'react-i18next';
import { Marca } from './Marca';
import {
  HomeIcon,
  CubeIcon,
  ArrowRightOnRectangleIcon,
  UserCircleIcon,
  ShieldCheckIcon,
} from '@heroicons/react/24/outline';

const Layout = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.auth);
  const { t } = useTranslation();

  // Quanta gent espera. Es demana en carregar i cada minut: si algú es registra mentre tens
  // TRAMA obert, el número apareix sol i no cal recordar-se'n d'anar a mirar.
  const [cua, setCua] = useState(0);
  useEffect(() => {
    if (user?.role !== 'admin') return undefined;
    let viu = true;
    const mira = () => adminService.summary()
      .then((d) => { if (viu) setCua(d.total || 0); })
      .catch(() => { /* si no arriba, el número es queda com estava */ });
    mira();
    const id = setInterval(mira, 60000);
    return () => { viu = false; clearInterval(id); };
  }, [user?.role]);

  // ELS ARQUETIPS NO SÓN AQUÍ (27/08/2026). Hi tenien entrada pròpia, al costat de Models i
  // d'Administració, i això els donava el pes d'una secció de l'aplicació. No ho són: un
  // arquetip és un diagrama de bucles causals. S'hi arriba des del panell de bucles del llenç
  // causal, que és on ja es mira l'estructura, i la ruta `/arquetips` segueix existint perquè
  // el reconeixement hi enllaça directament quan encerta.
  const navigation = [
    { name: t('nav.dashboard'), href: '/dashboard', icon: HomeIcon },
    { name: t('nav.models'), href: '/models', icon: CubeIcon },
    // Només per als admins, i amb un número al costat quan hi ha cua. Una cua que no es veu
    // és una cua que no s'atén, i aquí les dues cues acaben amb algú esperant a l'altra banda.
    ...(user?.role === 'admin'
      ? [{ name: t('nav.admin'), href: '/admin', icon: ShieldCheckIcon, comptador: true }]
      : []),
  ];

  const handleLogout = () => {
    dispatch(logout());
    navigate('/login');
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <nav className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16">
            <div className="flex">
              <div className="flex-shrink-0 flex items-center">
                <Marca mida={22} />
              </div>
              <div className="hidden sm:ml-6 sm:flex sm:space-x-8">
                {navigation.map((item) => {
                  const Icon = item.icon;
                  return (
                    <Link
                      key={item.name}
                      to={item.href}
                      className={`${
                        location.pathname === item.href
                          ? 'border-primary-500 text-gray-900'
                          : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'
                      } inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium`}
                    >
                      <Icon className="h-5 w-5 mr-2" />
                      {item.name}
                      {item.comptador && cua > 0 && (
                        <span className="ml-2 px-1.5 py-0.5 text-xs font-semibold rounded-full bg-warn-100 text-warn-800">
                          {cua}
                        </span>
                      )}
                    </Link>
                  );
                })}
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <SelectorIdioma />
              <Link to="/account"
                className="flex items-center text-sm text-gray-700 hover:text-gray-900 rounded-md px-2 py-1 hover:bg-gray-100"
                title={t('nav.account')}>
                <UserCircleIcon className="h-5 w-5 mr-2" />
                <span>{user?.name}</span>
                {/* El rol traduït, no el valor de la base de dades. «modeler» no vol dir res
                    per a qui l'està llegint. */}
                <span className="ml-2 px-2 py-1 bg-primary-100 text-primary-800 rounded-md text-xs">
                  {user?.role ? t(`roles.${user.role}`) : ''}
                </span>
              </Link>
              <button
                onClick={handleLogout}
                className="text-gray-500 hover:text-gray-700 flex items-center"
                title={t('nav.signOut')} aria-label={t('nav.signOut')}
              >
                <ArrowRightOnRectangleIcon className="h-5 w-5" />
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* `flex-1` perquè el peu quedi a baix de tot també quan la pàgina és curta, i no
          enganxat sota l'últim paràgraf. */}
      <main className="flex-1 w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Outlet />
      </main>

      <PeuStrategos />
    </div>
  );
};

export default Layout;